import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { useLang } from "../LanguageContext";
import placeholder from "../assets/placeholder";
import DisplayModeToggle from "../components/DisplayModeToggle";
import ImageUploadInput from "../components/ImageUploadInput";
import { LABEL_MODES, getLabelLines, getLabelText, useLabelMode } from '../hooks/useLabelMode';
import { copyText } from '../utils/clipboard';
import {
  validateMediaFiles,
  uploadMedia,
  getMediaErrorMessage,
} from '../utils/imageUpload';
import {
  normalizeMediaList,
  ensureMainMedia,
  addMediaItem,
  removeMediaByUrl,
  setMainMediaByUrl,
} from '../utils/media';

export default function CuttingsAdminPage() {
  const { t } = useLang();
  const token = localStorage.getItem("token");
  const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
  const decodeRole = (tok) => {
    if (!tok) return null;
    try {
      const payload = JSON.parse(atob(tok.split(".")[1]));
      return payload.role;
    } catch {
      return null;
    }
  };
  const role = decodeRole(token);
  const [cuttings, setCuttings] = useState([]);
  const [strains, setStrains] = useState([]);
  const [code, setCode] = useState("");
  const [strainId, setStrainId] = useState("");
  const [status, setStatus] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [logModal, setLogModal] = useState(false);
  const [selectedCutting, setSelectedCutting] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logEvent, setLogEvent] = useState("transplant");
  const [logNotes, setLogNotes] = useState("");
  const [logPhoto, setLogPhoto] = useState(null);
  const [strainView, setStrainView] = useState("both");
  const [labelMode, setLabelMode] = useLabelMode();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("date");
  const [mode, setMode] = useState("medium");
  const [group, setGroup] = useState(false);
  const [open, setOpen] = useState({});
  const [uploading, setUploading] = useState(false);

  const fetchCuttings = () => {
    fetch("/api/admin/cuttings", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setCuttings(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to fetch cuttings", err))
      .finally(() => setLoading(false));
  };

  const strainLabel = (s) => {
    if (strainView === "name") return s.name;
    if (strainView === "code") return s.slug;
    return `${s.name} (${s.slug})`;
  };

  const displayStrain = (c) => {
    if (strainView === "name") return c.strainName || c.strain;
    if (strainView === "code") return c.strainCode || c.strain;
    const name = c.strainName || c.strain;
    const code = c.strainCode || c.strain;
    return `${name} (${code})`;
  };

  const getCuttingLabelLines = (cutting) =>
    getLabelLines(labelMode, cutting.strainName || cutting.strain, cutting.code);

  const getCuttingLabelText = (cutting) =>
    getLabelText(labelMode, cutting.strainName || cutting.strain, cutting.code);

  const fetchStrains = () => {
    fetch("/api/admin/strains", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((d) => setStrains(Array.isArray(d) ? d : []))
      .catch(() => {});
  };

  useEffect(() => {
    if (token) {
      fetchCuttings();
      fetchStrains();
    } else setLoading(false);
  }, [token]);

  const resetForm = () => {
    setCode("");
    setStrainId("");
    setStatus("");
    setLocation("");
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = JSON.stringify({ code, strainId, status, location });
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    await fetch("/api/admin/cuttings", { method: "POST", headers, body });
    resetForm();
    fetchCuttings();
  };

  const startEdit = (c) => {
    setEditData({
      id: c._id,
      code: c.code,
      strainId: c.strainId || "",
      name: c.name || "",
      status: c.status || "",
      location: c.location || "",
      createdAt: c.createdAt ? new Date(c.createdAt).toISOString() : "",
      images: (() => {
        if (Array.isArray(c.images) && c.images.length) {
          return ensureMainMedia(normalizeMediaList(c.images));
        }
        if (c.photo) {
          return ensureMainMedia(
            normalizeMediaList([
              {
                url: c.photo,
                thumbnailUrl: c.photo,
                publicId: c.photoPublicId,
                isMain: true,
              },
            ])
          );
        }
        return [];
      })(),
    });
    setEditModal(true);
  };

  const duplicate = (c) => {
    const match = c.code.match(/^(.*?)(\d+)$/);
    let newCode = c.code;
    if (match) {
      const num = String(parseInt(match[2], 10) + 1).padStart(
        match[2].length,
        "0",
      );
      newCode = match[1] + num;
    }
    setCode(newCode);
    setStrainId(c.strainId || "");
    setStatus(c.status || "");
    setLocation(c.location || "");
  };

  const copyValue = async (event, text) => {
    event.preventDefault();
    if (event.stopPropagation) event.stopPropagation();
    if (!text) return;
    await copyText(text);
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await fetch(`/api/admin/cuttings/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchCuttings();
  };

  const updateCutting = async () => {
    const body = JSON.stringify({
      code: editData.code,
      strainId: editData.strainId,
      name: editData.name,
      status: editData.status,
      location: editData.location,
      createdAt: editData.createdAt,
      images: editData.images,
    });
    await fetch(`/api/admin/cuttings/${editData.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body,
    });
    setEditModal(false);
    setEditData({});
    fetchCuttings();
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const err = validateMediaFiles(files);
    if (err) {
      alert(getMediaErrorMessage(err, t));
      e.target.value = '';
      return;
    }
    setUploading(true);
    for (const file of files) {
      try {
        const d = await uploadMedia(file, token);
        setEditData((ed) => {
          const current = Array.isArray(ed.images) ? ed.images : [];
          return {
            ...ed,
            images: addMediaItem(current, {
              url: d.url,
              publicId: d.publicId,
              type: d.type,
              thumbnailUrl: d.thumbnailUrl,
            }),
          };
        });
      } catch (e2) {
        alert(getMediaErrorMessage(e2.message, t));
        break;
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeEditImage = (url) => {
    setEditData((d) => ({
      ...d,
      images: Array.isArray(d.images) ? removeMediaByUrl(d.images, url) : [],
    }));
  };

  const setEditMain = (url) => {
    setEditData((d) => ({
      ...d,
      images: Array.isArray(d.images) ? setMainMediaByUrl(d.images, url) : [],
    }));
  };


  const openLogs = async (c) => {
    setSelectedCutting(c);
    setLogModal(true);
    setLogEvent("transplant");
    setLogNotes("");
    setLogPhoto(null);
    try {
      const res = await fetch(`/api/admin/${c._id}/logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch logs", err);
    }
  };

  const addLog = async (e) => {
    e.preventDefault();
    const form = new FormData();
    form.append("event", logEvent);
    form.append("notes", logNotes);
    if (logPhoto) form.append("photo", logPhoto);
    await fetch(`/api/admin/${selectedCutting._id}/log`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    setLogNotes("");
    setLogPhoto(null);
    openLogs(selectedCutting);
  };

  const filtered = cuttings
    .filter(
      (c) =>
        c.code?.toLowerCase().includes(search.toLowerCase()) ||
        c.strainName?.toLowerCase().includes(search.toLowerCase()) ||
        c.shortId?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (sort === 'name')
        return (a.strainName || '').localeCompare(b.strainName || '');
      if (sort === 'internal')
        return (a.shortId || '').localeCompare(b.shortId || '');
      if (sort === 'id') return (a.code || '').localeCompare(b.code || '');
      const da = new Date(a.createdAt);
      const db = new Date(b.createdAt);
      return db - da;
    });

  // Previous QR download logic used canvas and is incompatible with the new
  // SVG-based QR component. TODO: implement SVG download if needed.

  if (!token || !(role === "superuser" || role === "admin")) {
    return (
      <div className="container mt-3">
        <p>{t("login")}...</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mt-3">
        <p>{t("loading")}</p>
      </div>
    );
  }

  return (
    <div className="container mt-3" style={{ maxWidth: 700 }}>
      <h1 className="mb-3">{t("manageCuttings")}</h1>
      <div className="row align-items-center mb-3">
        <div className="col-12 col-md-8 d-flex flex-wrap align-items-center mb-2 mb-md-0">
          <input
            className="form-control me-2 mb-2 mb-md-0"
            placeholder={t("search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="form-select w-auto me-2 mb-2 mb-md-0"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="date">{t("date")}</option>
            <option value="name">{t("marketName")}</option>
            <option value="internal">{t("internalNumber")}</option>
            <option value="id">ID</option>
          </select>
          <select
            className="form-select w-auto me-2 mb-2 mb-md-0"
            value={strainView}
            onChange={(e) => setStrainView(e.target.value)}
          >
            <option value="both">{t("both") || "Both"}</option>
            <option value="name">{t("byName") || "By name"}</option>
            <option value="code">{t("byCode") || "By code"}</option>
          </select>
          <select
            className="form-select w-auto me-2 mb-2 mb-md-0"
            value={labelMode}
            onChange={(e) => setLabelMode(e.target.value)}
          >
            <option value={LABEL_MODES.NAME_ONLY}>{t('labelModes.nameOnly')}</option>
            <option value={LABEL_MODES.NUMBER_ONLY}>{t('labelModes.numberOnly')}</option>
            <option value={LABEL_MODES.NAME_AND_NUMBER}>{t('labelModes.nameAndNumber')}</option>
          </select>
        </div>
        <div className="col-12 col-md-4 d-flex justify-content-md-end flex-wrap">
          <DisplayModeToggle mode={mode} setMode={setMode} />
          <div className="form-check ms-2 mt-2 mt-md-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="groupAdmin"
              checked={group}
              onChange={(e) => setGroup(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="groupAdmin">
              {t("strain")}
            </label>
          </div>
        </div>
      </div>
      
      <form onSubmit={submit} className="row g-2 align-items-end mb-3">
        <div className="col-12 col-sm-6 col-md-3">
          <input
            type="text"
            className="form-control"
            placeholder={t("code")}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
        </div>
        <div className="col-12 col-sm-6 col-md-3">
          <select
            className="form-select"
            value={strainId}
            onChange={(e) => setStrainId(e.target.value)}
            required
          >
            <option value="">{t("strain")}</option>
            {strains.map((s) => (
              <option key={s._id} value={s._id}>
                {strainLabel(s)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12 col-sm-6 col-md-2">
          <input
            type="text"
            className="form-control"
            placeholder={t("status")}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          />
        </div>
        <div className="col-12 col-sm-6 col-md-2">
          <input
            type="text"
            className="form-control"
            placeholder={t("location")}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-2 d-grid">
          <button
            className="btn btn-primary"
            type="submit"
            disabled={strains.length === 0}
          >
            {t("addCutting")}
          </button>
        </div>
      </form>
        {group ? (
          Object.entries(
            filtered.reduce((acc, c) => {
              const key = c.strainName || c.strain;
              if (!acc[key]) acc[key] = [];
              acc[key].push(c);
              return acc;
            }, {})
          ).map(([strain, list]) => (
            <div key={strain} className="mb-3">
              <button
                className="btn btn-link"
                onClick={() => setOpen((o) => ({ ...o, [strain]: !o[strain] }))}
              >
                {open[strain] ? '➖' : '➕'} {strain}
              </button>
              {open[strain] && (
                mode === 'table' ? (
                  <div className="table-responsive">
                    <table className="table">
                      <tbody>
                      {list.map((c) => (
                        <tr key={c._id}>
                          <td>{c.shortId || c._id}</td>
                          <td>
                          <span
                            className="d-inline-block text-truncate"
                            style={{ maxWidth: 220, wordBreak: 'break-word' }}
                            title={getCuttingLabelText(c)}
                          >
                            {getCuttingLabelText(c)}
                          </span>
                        </td>
                          <td>{c.status || '-'}</td>
                          <td>{c.location || '-'}</td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="row">
                    {list.map((c) => (
                      <div
                        key={c._id}
                        className={`${
                          mode === 'large'
                            ? 'col-12 col-md-6'
                            : 'col-12 col-md-6 col-lg-4'
                        } mb-3`}
                      >
                        <div className="card h-100">
                          <div className="card-body">
                            <h5 className="card-title d-flex justify-content-between">
                              {c.code}
                              <small className="text-muted">{c.shortId || c._id}</small>
                            </h5>
                            {(() => {
                              const main = Array.isArray(c.images)
                                ? c.images.find((i) => i.isMain) || c.images[0]
                                : null;
                              const previewUrl =
                                main?.type === 'video'
                                  ? main.thumbnailUrl || main.url
                                  : main?.url;
                              if (main?.type === 'video') {
                                return (
                                  <video
                                    src={main.url}
                                    poster={previewUrl || c.photo || placeholder}
                                    controls
                                    className="img-thumbnail mb-2"
                                    style={{ width: 150, height: 150, objectFit: 'cover' }}
                                  />
                                );
                              }
                              return (
                                <img
                                  src={previewUrl || c.photo || placeholder}
                                  alt="preview"
                                  loading="lazy"
                                  className="img-thumbnail mb-2"
                                  style={{ width: 150, height: 150, objectFit: 'cover' }}
                                />
                              );
                            })()}
                            <p className="mb-1">
                              <strong>{t('strain')}:</strong> {displayStrain(c)}
                            </p>
                            <p className="mb-1">
                              <strong>{t('status')}:</strong> {c.status || '-'}
                            </p>
                            <p className="mb-3">
                              <strong>{t('location')}:</strong> {c.location || '-'}
                            </p>
                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                              <a
                                href={`/strain/${c.strainCode || c.strain}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                🔗
                              </a>
                              <div className="text-center">
                                <QRCode value={`${baseUrl}/${encodeURIComponent(c.code)}`} size={64} />
                                <div
                                  className="small mt-1 w-100"
                                  style={{ wordBreak: 'break-word' }}
                                >
                                  {getCuttingLabelLines(c).map((line, idx) => (
                                    <div
                                      key={idx}
                                      className="w-100"
                                      style={{
                                        wordBreak: 'break-word',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={line}
                                    >
                                      {line}
                                    </div>
                                  ))}
                                </div>
                                <div className="d-flex gap-2 flex-wrap justify-content-center mt-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    aria-label={t('copyQrText')}
                                    onClick={(e) => copyValue(e, `${baseUrl}/${encodeURIComponent(c.code)}`)}
                                  >
                                    {t('copyQrText')}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    aria-label={t('copyLabelText')}
                                    onClick={(e) => copyValue(e, getCuttingLabelText(c))}
                                  >
                                    {t('copyLabelText')}
                                  </button>
                                </div>
                              </div>
                              </div>
                            <div>
                              <button
                                className="btn btn-sm btn-outline-secondary me-1"
                                onClick={() => openLogs(c)}
                              >
                                📜 {t('history')}
                              </button>
                              <button
                                className="btn btn-sm btn-outline-primary me-1"
                                onClick={() => startEdit(c)}
                              >
                                {t('edit')}
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary me-1"
                                onClick={() => duplicate(c)}
                              >
                                {t('duplicate') || 'Duplicate'}
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => del(c._id)}
                              >
                                {t('delete')}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          ))
      ) : mode === 'table' ? (
        <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>{t('id')}</th>
              <th>{t('code')}</th>
              <th>{t('status')}</th>
              <th>{t('location')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c._id}>
                <td>{c.shortId || c._id}</td>
                <td>
                <span
                  className="d-inline-block text-truncate"
                  style={{ maxWidth: 220, wordBreak: 'break-word' }}
                  title={getCuttingLabelText(c)}
                >
                  {getCuttingLabelText(c)}
                </span>
              </td>
                <td>{c.status || '-'}</td>
                <td>{c.location || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        <div className="row">
          {filtered.map((c) => (
            <div
              key={c._id}
              className={`${
                mode === 'large' ? 'col-12 col-md-6' : 'col-12 col-md-6 col-lg-4'
              } mb-3`}
            >
              <div className="card h-100">
                <div className="card-body">
                  <h5 className="card-title d-flex justify-content-between">
                    {c.code}
                    <small className="text-muted">{c.shortId || c._id}</small>
                  </h5>
                  {(() => {
                    const main = Array.isArray(c.images)
                      ? c.images.find((i) => i.isMain) || c.images[0]
                      : null;
                    const previewUrl =
                      main?.type === 'video'
                        ? main.thumbnailUrl || main.url
                        : main?.url;
                    if (main?.type === 'video') {
                      return (
                        <video
                          src={main.url}
                          poster={previewUrl || c.photo || placeholder}
                          controls
                          className="img-thumbnail mb-2"
                          style={{ width: 150, height: 150, objectFit: 'cover' }}
                        />
                      );
                    }
                    return (
                      <img
                        src={previewUrl || c.photo || placeholder}
                        alt="preview"
                        loading="lazy"
                        className="img-thumbnail mb-2"
                        style={{ width: 150, height: 150, objectFit: 'cover' }}
                      />
                    );
                  })()}
                  <p className="mb-1">
                    <strong>{t("strain")}:</strong> {displayStrain(c)}
                  </p>
                  <p className="mb-1">
                    <strong>{t("status")}:</strong> {c.status || "-"}
                  </p>
                  <p className="mb-3">
                    <strong>{t("location")}:</strong> {c.location || "-"}
                  </p>
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <a
                      href={`/strain/${c.strainCode || c.strain}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      🔗
                    </a>
                    <div className="text-center">
                      <QRCode value={`${baseUrl}/${encodeURIComponent(c.code)}`} size={64} />
                      <div className="small mt-1">
                        {getCuttingLabelLines(c).map((line, idx) => (
                          <div key={idx}>{line}</div>
                        ))}
                      </div>
                      <div className="d-flex gap-2 flex-wrap justify-content-center mt-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                      aria-label={t('copyQrText')}
                      onClick={(e) => copyValue(e, `${baseUrl}/${encodeURIComponent(c.code)}`)}
                        >
                          {t('copyQrText')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={(e) => copyValue(e, getCuttingLabelText(c))}
                        >
                          {t('copyLabelText')}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <button
                      className="btn btn-sm btn-outline-secondary me-1"
                      onClick={() => openLogs(c)}
                    >
                      📜 {t("history")}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-primary me-1"
                      onClick={() => startEdit(c)}
                    >
                      {t("edit")}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary me-1"
                      onClick={() => duplicate(c)}
                    >
                      {t("duplicate") || "Duplicate"}
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => del(c._id)}
                    >
                      {t("delete")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
              ))}
          </div>
        )}
        {editModal && (
          <div className="modal d-block" tabIndex="-1" role="dialog">
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("edit")}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setEditModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="form-control mb-2"
                  value={editData.code}
                  onChange={(e) =>
                    setEditData({ ...editData, code: e.target.value })
                  }
                  placeholder={t("code")}
                />
                <select
                  className="form-select mb-2"
                  value={editData.strainId}
                  onChange={(e) =>
                    setEditData({ ...editData, strainId: e.target.value })
                  }
                >
                  <option value="">{t("strain")}</option>
                  {strains.map((s) => (
                    <option key={s._id} value={s._id}>
                      {strainLabel(s)}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-control mb-2"
                  value={editData.name || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, name: e.target.value })
                  }
                  placeholder={t("name")}
                />
                <input
                  type="text"
                  className="form-control mb-2"
                  value={editData.status}
                  onChange={(e) =>
                    setEditData({ ...editData, status: e.target.value })
                  }
                  placeholder={t("status")}
                />
                <input
                  type="text"
                  className="form-control mb-2"
                  value={editData.location}
                  onChange={(e) =>
                    setEditData({ ...editData, location: e.target.value })
                  }
                  placeholder={t("location")}
                />
                <input
                  type="datetime-local"
                  className="form-control mb-2"
                  value={
                    editData.createdAt ? editData.createdAt.slice(0, 16) : ""
                  }
                  onChange={(e) =>
                    setEditData({ ...editData, createdAt: e.target.value })
                  }
                />
                {editData.images &&
                  editData.images.map((img) => (
                    <div
                      key={img.publicId || img.url}
                      className="position-relative d-inline-block me-2 mb-2"
                      style={{ maxWidth: 120 }}
                    >
                      {img.type === 'video' ? (
                        <video
                          src={img.url}
                          poster={img.thumbnailUrl || img.url}
                          controls
                          className="img-fluid"
                        />
                      ) : (
                        <img
                          src={img.url}
                          alt="preview"
                          loading="lazy"
                          className="img-fluid"
                        />
                      )}
                      {img.isMain && (
                        <span className="badge bg-primary position-absolute top-0 start-0">★</span>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0"
                        onClick={() => removeEditImage(img.url)}
                      >
                        &times;
                      </button>
                      {!img.isMain && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary position-absolute bottom-0 start-0"
                          onClick={() => setEditMain(img.url)}
                        >
                          ★
                        </button>
                      )}
                    </div>
                  ))}
                <ImageUploadInput
                  multiple
                  className="form-control mb-2"
                  onChange={handleFiles}
                />
                {uploading && (
                  <div className="mb-2">
                    <span className="spinner-border spinner-border-sm me-2" />
                    {t('photoUploading')}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditModal(false)}
                >
                  {t("cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={updateCutting}
                >
                  {t("save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {logModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("history")}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setLogModal(false)}
                ></button>
              </div>
              <div
                className="modal-body"
                style={{ maxHeight: "70vh", overflowY: "auto" }}
              >
                {logs.map((l) => (
                  <div key={l._id} className="mb-2 border-bottom">
                    <div>{new Date(l.createdAt).toLocaleString()}</div>
                    <div>{l.event}</div>
                    {l.notes && <div>{l.notes}</div>}
                    {l.photo && (
                      <img src={l.photo} alt="" loading="lazy" className="img-fluid" />
                    )}
                  </div>
                ))}
                <form onSubmit={addLog} className="mt-3">
                  <select
                    className="form-select mb-2"
                    value={logEvent}
                    onChange={(e) => setLogEvent(e.target.value)}
                  >
                    <option value="transplant">{t("transplant")}</option>
                    <option value="treatment">{t("treatment")}</option>
                    <option value="move">{t("move")}</option>
                  </select>
                  <textarea
                    className="form-control mb-2"
                    placeholder={t("comment")}
                    value={logNotes}
                    onChange={(e) => setLogNotes(e.target.value)}
                  />
                  <ImageUploadInput
                    className="form-control mb-2"
                    onChange={(e) => setLogPhoto(e.target.files[0])}
                  />
                  <button type="submit" className="btn btn-primary">
                    {t("addLog")}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
